/**
 * ツールバー部品
 *
 * どれも props を受けて DOM と更新メソッドを返すだけ。append・状態同期の登録・editor コマンドは
 * 呼び出し側の責務で、ここには editor も可変状態も持たない
 *
 * クラス名は SCSS と Gauge の Web テストが参照する外部契約なので変更しない
 */
import { icon } from './icons';

/** ツールバー部品の共通形。el を append するのは呼び出し側 */
export interface ToolbarItem {
  el: HTMLElement;
}

/** 押下状態を持つ部品 (コマンドボタン / トリガーボタン) */
export interface PressableItem extends ToolbarItem {
  el: HTMLButtonElement;
  /** aria-pressed を更新する。呼ばなければ属性は付かない (中立表示のまま) */
  setActive(active: boolean): void;
}

/** フォーカス移動を止める。ツールバー操作でエディタの選択範囲を失わないため */
const preventFocusSteal = (el: HTMLElement): void => {
  el.addEventListener('pointerdown', (e) => e.preventDefault());
};

/** アイコンだけの素のボタン。click は呼び出し側で付ける。他の部品の土台にもなる */
export const createIconButton = ({
  iconName,
  label,
  extraClass,
}: {
  iconName: string;
  label: string;
  extraClass?: string;
}): HTMLButtonElement => {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'codmon-rte__item codmon-rte__btn' + (extraClass ? ' ' + extraClass : '');
  el.setAttribute('aria-label', label);
  // 素の title だと OS 標準の見た目になるので、SCSS が拾える data-tooltip で吹き出しを出す
  el.setAttribute('data-tooltip', label);
  el.innerHTML = `<span class="codmon-rte__icon">${icon(iconName)}</span>`;
  preventFocusSteal(el);
  return el;
};

/** コマンドトグルボタン (太字 / 斜体 / 下線 / 装飾解除 / リスト) */
export const createCommandButton = ({
  iconName,
  label,
  onClick,
}: {
  iconName: string;
  label: string;
  onClick: () => void;
}): PressableItem => {
  const el = createIconButton({ iconName, label });
  el.addEventListener('click', onClick);
  return {
    el,
    setActive: (active) => el.setAttribute('aria-pressed', active ? 'true' : 'false'),
  };
};

/** ポップアップを開くボタン (文字色 / 段落 / 行の高さ / テーブル / リンク)。chevron 付き */
export const createTriggerButton = ({
  iconName,
  label,
  onClick,
}: {
  iconName: string;
  label: string;
  onClick: () => void;
}): PressableItem & {
  /** 文字色・背景色をアイコンへ反映する */
  setIconColors(colors: { color?: string | null; backgroundColor?: string | null }): void;
} => {
  const el = createIconButton({ iconName, label, extraClass: 'codmon-rte__btn--trigger' });
  el.insertAdjacentHTML('beforeend', `<span class="codmon-rte__chevron">${icon('chevron')}</span>`);
  el.addEventListener('click', onClick);
  return {
    el,
    setActive: (active) => el.setAttribute('aria-pressed', active ? 'true' : 'false'),
    setIconColors: ({ color, backgroundColor }) => {
      const iconWrap = el.querySelector<HTMLElement>('.codmon-rte__icon');
      if (!iconWrap) return;
      const svg = iconWrap.querySelector<SVGElement>('svg');
      if (svg) svg.style.color = color || '';
      iconWrap.style.backgroundColor = backgroundColor || '';
    },
  };
};

/** 固定幅の SelectBox (フォント / 段落 / 文字サイズ)。選択肢の一覧は createSelectPopup が作る */
export const createSelectBox = ({
  ref,
  label,
  onToggle,
}: {
  /** font / paragraph / fontsize。クラス名の接尾辞になる */
  ref: string;
  /** 何を選ぶ欄かを示すツールチップ。読み上げ名は setLabel が現在値を足して組む */
  label: string;
  onToggle: () => void;
}): ToolbarItem & {
  /** 表示文字と読み上げ名を更新する。選択肢に一致しなければ placeholder 表示にする */
  setLabel(text: string, isPlaceholder: boolean): void;
} => {
  const el = document.createElement('div');
  el.className = 'codmon-rte__item codmon-rte__select codmon-rte__select--' + ref;
  // 吹き出しはセル全体をホバー範囲にしたいので外枠へ、読み上げ名は実際に押せる button へ付ける
  el.setAttribute('data-tooltip', label);
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'codmon-rte__select-btn';
  // 値が入るまでは欄の名前だけ。確定後は setLabel が現在値を足す
  button.setAttribute('aria-label', label);
  const textEl = document.createElement('span');
  textEl.className = 'codmon-rte__select-text';
  button.appendChild(textEl);
  button.insertAdjacentHTML('beforeend', `<span class="codmon-rte__chevron">${icon('chevron')}</span>`);
  preventFocusSteal(button);
  button.addEventListener('click', onToggle);
  el.appendChild(button);
  return {
    el,
    setLabel: (text, isPlaceholder) => {
      textEl.textContent = text;
      el.classList.toggle('codmon-rte__select--placeholder', isPlaceholder);
      // aria-label は中の表示文字を上書きするので、固定値だと選択中の値が読み上げられない。
      // 値が未確定のときは読む値が無いので欄の名前だけにする
      button.setAttribute('aria-label', isPlaceholder ? label : label + ' ' + text);
    },
  };
};

/** ボタン群の区切り線 */
export const createDivider = (): HTMLElement => {
  const el = document.createElement('span');
  el.className = 'codmon-rte__sep';
  return el;
};
